import { Constants, EffectRenderer, EffectWrapper, RenderTargetTexture, Texture, type Scene } from "@babylonjs/core";
import { SHIP } from "@tiny-seas/shared";
import type { ShipMotion, WakeParams } from "./waves.ts";

/**
 * Ships and the sea, simulated: a camera-centred window of linear deep-water waves that hulls
 * press on. Each frame the hulls' pressure sets the height the water rests at under them, then
 * the whole field advances exactly in Fourier space (every wavenumber k turns at ω = √(g·k)), so
 * bow waves, the Kelvin wake's 19.5° arms and the rings round a slamming hull all emerge from
 * the water rather than being drawn. Foam forms where the simulated waves break (downward
 * acceleration beyond g/2, after Longuet-Higgins) and where hulls churn the water.
 *
 * Textures (float32): `surface` holds (h, ∂h/∂t) in metres and m/s over `window`; `display`
 * holds (foam, h, ∂h/∂x, ∂h/∂z) for the water shader's single fetch. Both follow the camera in
 * whole texels. With no hull in the window for a while the simulation idles and `active` is false.
 */

/**
 * A hull in the water: flat pose, speed (m/s), bow/stern immersion (m) and the swell's plane under it.
 * `id` matches it across frames.
 */
export interface HullSource { id: number; x: number; z: number; heading: number; speed: number; bow: number; stern: number; water: Readonly<ShipMotion> }

/** Hulls the simulation takes (nearest to the camera first). */
export const SIM_HULLS = 8;
/** Grid size (a power of 16, for the radix-16 FFT) and spacing: a 512 m window resolving waves down to 4 m, the ocean mesh's own limit. */
export const SIM_SIZE = 256;
const RADIX = 16;
export const SIM_SPACING = 2;
export const SIM_EXTENT = SIM_SIZE * SIM_SPACING;

const VERTEX = `
precision highp float;
attribute vec2 position;
varying vec2 vUV;
void main(void) {
  vUV = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0.0, 1.0);
}`;

const COMMON = `
precision highp float;
// Fragment samplers default to lowp, which ANGLE's Vulkan backend reads at half precision:
// the unnormalised spectrum (≈10⁵) would overflow to Inf.
precision highp sampler2D;
#define SIZE ${SIM_SIZE.toFixed(1)}
#define HULLS ${SIM_HULLS}
#define HALF_LENGTH ${(SHIP.length / 2).toFixed(2)}
#define HALF_BEAM ${(SHIP.beam / 2).toFixed(2)}
varying vec2 vUV;
vec4 texel(sampler2D map, vec2 coord) { return texelFetch(map, ivec2(coord), 0); }
// Immersed depth of a hull at p: full beam amidships narrowing to bow and stern, a rounded
// section, and the draft blended from stern to bow. Edges ease over a couple of metres, as the
// pressure of water flowing round a hull does; a sharp footprint radiates breaking ripples.
float hullDepth(vec2 p, vec2 centre, float heading, float bow, float stern) {
  vec2 f = vec2(sin(heading), cos(heading)), r = vec2(f.y, -f.x), d = p - centre;
  float s = dot(d, f) / HALF_LENGTH, n = dot(d, r) / HALF_BEAM;
  float width = sqrt(max(0.0, 1.0 - s * s * s * s));
  float inside = (1.0 - smoothstep(width - 0.5, width + 0.5, abs(n))) * (1.0 - smoothstep(0.8, 1.2, abs(s)));
  return inside * mix(stern, bow, clamp(s * 0.5 + 0.5, 0.0, 1.0)) * (1.0 - 0.5 * n * n);
}`;

/**
 * Moves last frame's surface with the window, damps it near the edges, and lays the hulls'
 * pressure (as the depth c·D it holds the water down by) in the second complex channel.
 */
const PREPARE = `${COMMON}
uniform sampler2D surface;
// (centre x, centre z, extent, dt); window shift in texels; edge sponge rate (1/s).
uniform vec4 window;
uniform vec2 shift;
uniform float sponge;
// Per hull: (x, z, heading, coupling) and immersion (bow, stern, 0, 0).
uniform vec4 hulls[HULLS];
uniform vec4 immersion[HULLS];
uniform float hullCount;
void main(void) {
  vec2 coord = floor(gl_FragCoord.xy), from = coord + shift;
  vec2 state = from.x < 0.0 || from.y < 0.0 || from.x >= SIZE || from.y >= SIZE ? vec2(0.0) : texel(surface, from).xy;
  vec2 p = window.xy + (vUV - 0.5) * window.z;
  float pressed = 0.0;
  for (int i = 0; i < HULLS; i++) {
    if (float(i) >= hullCount) break;
    vec4 h = hulls[i];
    pressed += hullDepth(p, h.xy, h.z, immersion[i].x, immersion[i].y) * h.w;
  }
  float edge = min(min(vUV.x, vUV.y), min(1.0 - vUV.x, 1.0 - vUV.y));
  state *= mix(exp(-sponge * window.w), 1.0, smoothstep(0.0, 0.12, edge));
  gl_FragColor = vec4(state, pressed, 0.0);
}`;

/**
 * One radix-8 Stockham pass over rows or columns, natural order in and out: output o of a pass
 * merging sub-transforms of size p = size/8 sums inputs i + s·N/8 (i = ⌊o/size⌋·p + o mod p)
 * turned by e^(±2πi·s·(o mod size)/size).
 */
const FFT = `${COMMON}
uniform sampler2D source;
// (merged size, 1 = rows / 0 = columns, -1 forward / +1 inverse, output scale)
uniform vec4 pass;
void main(void) {
  vec2 coord = floor(gl_FragCoord.xy);
  bool rows = pass.y > 0.5;
  float index = rows ? coord.x : coord.y, part = pass.x / ${RADIX.toFixed(1)};
  float first = floor(index / pass.x) * part + mod(index, part);
  float angle = pass.z * 6.283185307 * mod(index, pass.x) / pass.x;
  vec4 sum = vec4(0.0);
  for (int s = 0; s < ${RADIX}; s++) {
    float at = first + float(s) * (SIZE / ${RADIX.toFixed(1)});
    vec4 v = texel(source, rows ? vec2(at, coord.y) : vec2(coord.x, at));
    float a = angle * float(s);
    vec2 w = vec2(cos(a), sin(a));
    sum += vec4(v.x * w.x - v.y * w.y, v.x * w.y + v.y * w.x, v.z * w.x - v.w * w.y, v.z * w.y + v.w * w.x);
  }
  gl_FragColor = sum * pass.w;
}`;

/**
 * Exact linear evolution of Z = ĥ + i·∂ĥ/∂t: split Z(k) and Z(−k) into the real fields' spectra,
 * rotate each wavenumber by ω·dt with ω² = g·|k|, damp, and recombine. Hull pressure P (second
 * channel, as the depth P/ρg = c·D̂ it holds the water down by) moves each wavenumber's rest
 * height to −c·D̂, so the surface swings about that: a moving pressure patch, Havelock's source
 * of the Kelvin wake.
 */
const EVOLVE = `${COMMON}
uniform sampler2D spectrum;
// (spacing m, dt s, damping 1/s, 0)
uniform vec4 step;
void main(void) {
  vec2 coord = floor(gl_FragCoord.xy);
  vec2 f = vec2(coord.x < SIZE * 0.5 ? coord.x : coord.x - SIZE, coord.y < SIZE * 0.5 ? coord.y : coord.y - SIZE);
  float k = length(f) * 6.283185307 / (SIZE * step.x);
  if (k == 0.0) { gl_FragColor = vec4(0.0); return; }
  vec4 here = texel(spectrum, coord);
  vec2 z = here.xy, zm = texel(spectrum, mod(SIZE - coord, SIZE)).xy, pressed = here.zw;
  vec2 conj = vec2(zm.x, -zm.y);
  vec2 h = 0.5 * (z + conj) + pressed, d = 0.5 * (z - conj);
  vec2 v = vec2(d.y, -d.x);
  float w = sqrt(9.81 * k), c = cos(w * step.y), s = sin(w * step.y);
  vec2 hn = h * c + v * (s / w), vn = v * c - h * (w * s);
  // Shorter waves lose energy faster; the last octave below the grid's Nyquist limit is damped out.
  float nyquist = 3.14159265 / step.x;
  float decay = exp(-(step.z + 0.004 * k * k + 3.0 * smoothstep(0.55 * nyquist, nyquist, k)) * step.y);
  hn = hn * decay - pressed;
  vn *= decay;
  gl_FragColor = vec4(hn + vec2(-vn.y, vn.x), 0.0, 0.0);
}`;

/**
 * Foam cover: last frame's foam moved with the window, spread and decayed, plus foam where the
 * simulated surface breaks and where hulls churn the water along their sides and stern. Also
 * writes the height and its gradient beside it, so the water shader reads one texel.
 */
const FOAM = `${COMMON}
uniform sampler2D foam;
uniform sampler2D before;
uniform sampler2D surface;
uniform vec4 window;
uniform vec2 shift;
// (decay this step, spread, breaking foam rate, churn rate)
uniform vec4 shape;
uniform vec4 hulls[HULLS];
uniform vec4 hullSpeeds[HULLS];
uniform float hullCount;
float old(vec2 coord) {
  vec2 from = coord + shift;
  return from.x < 0.0 || from.y < 0.0 || from.x >= SIZE || from.y >= SIZE ? 0.0 : texel(foam, from).x;
}
float height(vec2 coord) { return texel(surface, clamp(coord, vec2(0.0), vec2(SIZE - 1.0))).x; }
void main(void) {
  vec2 coord = floor(gl_FragCoord.xy);
  float around = (old(coord + vec2(1.0, 0.0)) + old(coord - vec2(1.0, 0.0)) + old(coord + vec2(0.0, 1.0)) + old(coord - vec2(0.0, 1.0))) * 0.25;
  float cover = mix(old(coord), around, shape.y) * shape.x;
  vec2 now = texel(surface, coord).xy;
  float acceleration = (now.y - texel(before, coord).y) / max(window.w, 1e-4);
  // Crests break when the water falls away faster than half of gravity.
  cover += shape.z * smoothstep(0.5, 0.8, -acceleration / 9.81) * step(0.0, now.x) * window.w * 4.0;
  vec2 p = window.xy + (vUV - 0.5) * window.z;
  for (int i = 0; i < HULLS; i++) {
    if (float(i) >= hullCount) break;
    vec4 h = hulls[i];
    vec2 f = vec2(sin(h.z), cos(h.z)), r = vec2(f.y, -f.x), d = p - h.xy;
    float s = dot(d, f) / HALF_LENGTH, n = abs(dot(d, r)) / HALF_BEAM;
    // Turbulence clings to the hull's sides and trails off its stern.
    float side = (1.0 - smoothstep(0.0, 0.6, abs(n - sqrt(max(0.0, 1.0 - s * s * s * s))))) * (1.0 - smoothstep(0.9, 1.1, abs(s)));
    float stern = (1.0 - smoothstep(0.2, 0.9, n)) * smoothstep(-1.3, -1.0, s) * (1.0 - smoothstep(-1.0, -0.8, s));
    cover += shape.w * (side + stern) * smoothstep(0.5, 6.0, hullSpeeds[i].x) * window.w;
  }
  vec2 gradient = vec2(height(coord + vec2(1.0, 0.0)) - height(coord - vec2(1.0, 0.0)),
    height(coord + vec2(0.0, 1.0)) - height(coord - vec2(0.0, 1.0))) / (2.0 * window.z / SIZE);
  gl_FragColor = vec4(min(cover, 1.0), now.x, gradient);
}`;

function floatTarget(scene: Scene, name: string, sampling: number): RenderTargetTexture {
  const target = new RenderTargetTexture(name, SIM_SIZE, scene,
    { type: Constants.TEXTURETYPE_FLOAT, generateMipMaps: false, generateDepthBuffer: false, samplingMode: sampling, gammaSpace: false });
  target.wrapU = target.wrapV = Texture.CLAMP_ADDRESSMODE;
  return target;
}

/** The simulation and its render targets; `update` runs every pass for one step. */
export class WaterSim {
  /** False where the GPU cannot render to 32-bit float textures (the unnormalised spectrum overflows half floats): hulls then leave the water undisturbed. */
  readonly supported: boolean;
  private readonly renderer: EffectRenderer;
  private readonly prepare: EffectWrapper;
  private readonly fft: EffectWrapper;
  private readonly evolve: EffectWrapper;
  private readonly foamPass: EffectWrapper;
  private readonly prepared: RenderTargetTexture;
  private readonly work: [RenderTargetTexture, RenderTargetTexture];
  private readonly foamTargets: [RenderTargetTexture, RenderTargetTexture];
  private surfaceTarget: RenderTargetTexture;
  private foamCurrent = 0;
  private source!: RenderTargetTexture;
  private readonly passArgs = [RADIX, 1, -1, 1];
  private centreX = 0;
  private centreZ = 0;
  private shiftX = 0;
  private shiftZ = 0;
  private dt = 0;
  private params!: Readonly<WakeParams>;
  private readonly hulls = new Array<number>(SIM_HULLS * 4).fill(0);
  private readonly immersion = new Array<number>(SIM_HULLS * 4).fill(0);
  private readonly speeds = new Array<number>(SIM_HULLS * 4).fill(0);
  private hullCount = 0;
  /** Seconds since a hull was last in the window; starts idle. */
  private quiet = Infinity;
  private running = false;
  /** Frames per step (graphics preset): skipped frames' time joins the next step, which evolves exactly over it. */
  stepEvery = 1;
  private skipped = 0;
  private pending = 0;
  /** Per hull id: last position and how far (0–1) its pressure has eased in. */
  private readonly present = new Map<number, { x: number; z: number; weight: number }>();

  constructor(scene: Scene) {
    const engine = scene.getEngine();
    const caps = engine.getCaps();
    this.supported = !!caps.textureFloatRender;
    // Passes read exact texels (texelFetch); filtering only serves the water shader and spray.
    const sampling = caps.textureFloatLinearFiltering ? Texture.BILINEAR_SAMPLINGMODE : Texture.NEAREST_SAMPLINGMODE;
    this.prepared = floatTarget(scene, "sea-prepared", sampling);
    this.work = [floatTarget(scene, "sea-work-a", sampling), floatTarget(scene, "sea-work-b", sampling)];
    this.foamTargets = [floatTarget(scene, "sea-foam-a", sampling), floatTarget(scene, "sea-foam-b", sampling)];
    this.surfaceTarget = this.work[0];
    this.renderer = new EffectRenderer(engine);
    const wrapper = (name: string, fragmentShader: string, uniformNames: string[], samplerNames: string[]): EffectWrapper =>
      new EffectWrapper({ engine, name, vertexShader: VERTEX, fragmentShader, uniformNames, samplerNames });
    this.prepare = wrapper("seaPrepare", PREPARE, ["window", "shift", "sponge", "hulls", "immersion", "hullCount"], ["surface"]);
    this.fft = wrapper("seaFft", FFT, ["pass"], ["source"]);
    this.evolve = wrapper("seaEvolve", EVOLVE, ["step"], ["spectrum"]);
    this.foamPass = wrapper("seaFoam", FOAM, ["window", "shift", "shape", "hulls", "hullSpeeds", "hullCount"], ["foam", "before", "surface"]);
    this.prepare.onApplyObservable.add(() => {
      const e = this.prepare.effect;
      e.setTexture("surface", this.surfaceTarget);
      e.setFloat4("window", this.centreX, this.centreZ, SIM_EXTENT, this.dt);
      e.setFloat2("shift", this.shiftX, this.shiftZ);
      e.setFloat("sponge", 1.5);
      e.setArray4("hulls", this.hulls);
      e.setArray4("immersion", this.immersion);
      e.setFloat("hullCount", this.hullCount);
    });
    this.fft.onApplyObservable.add(() => {
      this.fft.effect.setTexture("source", this.source);
      this.fft.effect.setFloat4("pass", this.passArgs[0]!, this.passArgs[1]!, this.passArgs[2]!, this.passArgs[3]!);
    });
    this.evolve.onApplyObservable.add(() => {
      this.evolve.effect.setTexture("spectrum", this.source);
      this.evolve.effect.setFloat4("step", SIM_SPACING, this.dt, this.params.damping, 0);
    });
    this.foamPass.onApplyObservable.add(() => {
      const e = this.foamPass.effect, p = this.params;
      e.setTexture("foam", this.foamTargets[this.foamCurrent]!);
      e.setTexture("before", this.prepared);
      e.setTexture("surface", this.surfaceTarget);
      e.setFloat4("window", this.centreX, this.centreZ, SIM_EXTENT, this.dt);
      e.setFloat2("shift", this.shiftX, this.shiftZ);
      e.setFloat4("shape", Math.exp(-this.dt / Math.max(p.lifetime, 0.1)), Math.min(1, p.spread * this.dt * 4), p.breaking, p.churn);
      e.setArray4("hulls", this.hulls);
      e.setArray4("hullSpeeds", this.speeds);
      e.setFloat("hullCount", this.hullCount);
    });
  }

  /** (h, ∂h/∂t) in metres and m/s over `window`. */
  get surface(): RenderTargetTexture {
    return this.surfaceTarget;
  }

  /** (foam cover, h m, ∂h/∂x, ∂h/∂z) over `window`. */
  get display(): RenderTargetTexture {
    return this.foamTargets[this.foamCurrent]!;
  }

  /** False while idle (no hull in the window for a while) or unsupported: the water ignores the textures. */
  get active(): boolean {
    return this.supported && this.running;
  }

  /** (centre x, centre z, extent) of the simulated window. */
  get window(): [number, number, number] {
    return [this.centreX, this.centreZ, SIM_EXTENT];
  }

  /** How far (m) the window moved in the last `update` (zero when it skipped its step). */
  get shift(): [number, number] {
    return [this.shiftX * SIM_SPACING, this.shiftZ * SIM_SPACING];
  }

  private ready(): boolean {
    return this.supported && this.prepare.effect.isReady() && this.fft.effect.isReady() && this.evolve.effect.isReady() && this.foamPass.effect.isReady();
  }

  /** Advances the sea by `dt` around (cameraX, cameraZ) with `hulls` (nearest first), stepping every `stepEvery` frames. */
  update(dt: number, cameraX: number, cameraZ: number, hulls: readonly HullSource[], params: Readonly<WakeParams>): void {
    if (dt <= 0 || !this.ready()) return;
    const reach = SIM_EXTENT / 2 + SHIP.length;
    let inside = 0;
    for (let i = 0; i < hulls.length && inside < SIM_HULLS; i++) {
      if (Math.abs(hulls[i]!.x - cameraX) < reach && Math.abs(hulls[i]!.z - cameraZ) < reach) inside++;
    }
    // Once hulls have been gone long enough for their waves and foam to fade, the sea stops simulating.
    this.quiet = inside > 0 ? 0 : this.quiet + dt;
    this.running = this.quiet < Math.max(20, params.lifetime * 3);
    this.shiftX = this.shiftZ = 0;
    if (!this.running) {
      this.skipped = this.pending = 0;
      return;
    }
    this.pending += dt;
    if (++this.skipped < this.stepEvery) return;
    // Long stalls (a hidden tab) advance at most 0.1 s per frame.
    this.dt = Math.min(this.pending, 0.1 * this.skipped);
    this.skipped = this.pending = 0;
    this.params = params;
    const centreX = Math.round(cameraX / SIM_SPACING) * SIM_SPACING, centreZ = Math.round(cameraZ / SIM_SPACING) * SIM_SPACING;
    this.shiftX = Math.round((centreX - this.centreX) / SIM_SPACING);
    this.shiftZ = Math.round((centreZ - this.centreZ) / SIM_SPACING);
    this.centreX = centreX;
    this.centreZ = centreZ;
    // Hulls come nearest first, so those in the window lead the list.
    this.hullCount = inside;
    for (const id of this.present.keys()) {
      if (!hulls.some((h, i) => i < this.hullCount && h.id === id)) this.present.delete(id);
    }
    for (let i = 0; i < this.hullCount; i++) {
      const h = hulls[i]!;
      let seen = this.present.get(h.id);
      // A hull that arrives or jumps (respawn, teleport) eases its pressure in over a second rather than slamming the sea.
      if (!seen || Math.hypot(h.x - seen.x, h.z - seen.z) > 30) this.present.set(h.id, seen = { x: h.x, z: h.z, weight: 0 });
      seen.x = h.x; seen.z = h.z;
      seen.weight = Math.min(1, seen.weight + this.dt);
      this.hulls.splice(i * 4, 4, h.x, h.z, h.heading, params.displacement * seen.weight);
      this.immersion.splice(i * 4, 4, h.bow, h.stern, 0, 0);
      this.speeds.splice(i * 4, 4, Math.abs(h.speed), 0, 0, 0);
    }

    this.renderer.render(this.prepare, this.prepared);
    // Forward transform rows then columns, evolve, inverse transform: 2·log₈N + 1 + 2·log₈N passes.
    this.source = this.prepared;
    let target = 0;
    const run = (wrapper: EffectWrapper): void => {
      const out = this.work[target]!;
      this.renderer.render(wrapper, out);
      this.source = out;
      target = 1 - target;
    };
    for (const direction of [-1, 1]) {
      for (const rows of [1, 0]) {
        for (let size = RADIX; size <= SIM_SIZE; size *= RADIX) {
          const last = direction === 1 && rows === 0 && size === SIM_SIZE;
          const args = this.passArgs;
          args[0] = size; args[1] = rows; args[2] = direction; args[3] = last ? 1 / (SIM_SIZE * SIM_SIZE) : 1;
          run(this.fft);
        }
      }
      if (direction === -1) run(this.evolve);
    }
    this.surfaceTarget = this.source;
    const next = 1 - this.foamCurrent;
    this.renderer.render(this.foamPass, this.foamTargets[next]!);
    this.foamCurrent = next;
  }

  dispose(): void {
    for (const w of [this.prepare, this.fft, this.evolve, this.foamPass]) w.dispose();
    this.renderer.dispose();
    for (const t of [this.prepared, ...this.work, ...this.foamTargets]) t.dispose();
  }
}
